import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useBilling } from '../../hooks/useBilling';
import { Crown, Calendar, CheckCircle, Settings, CreditCard } from 'lucide-react';

interface CurrentPlanProps {
  plan: {
    name: string;
    price: number;
    billingCycle: string;
    status: string;
    nextBilling: string | null;
    trialEndsAt?: string | null;
    features: string[];
  };
}

export function CurrentPlan({ plan }: CurrentPlanProps) {
  const { createPortalSession, isCreatingPortal } = useBilling();

  const handleManagePlan = async () => {
    try {
      await createPortalSession({
        returnUrl: window.location.href
      });
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'trial': return 'secondary';
      case 'cancelled': return 'destructive';
      case 'past_due': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'trial': return <Settings className="w-3 h-3 mr-1" />;
      case 'cancelled': return <Settings className="w-3 h-3 mr-1" />;
      case 'past_due': return <CreditCard className="w-3 h-3 mr-1" />;
      default: return <Settings className="w-3 h-3 mr-1" />;
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Current Plan</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Your active subscription</p>
          </div>
        </div>
        <Badge variant={getStatusColor(plan.status)}>
          {getStatusIcon(plan.status)}
          {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
        </Badge>
      </div>

      <div className="space-y-4">
        {/* Plan Details */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900 dark:text-white">{plan.name}</h4>
            <div className="text-right">
              <span className="text-2xl font-bold text-gray-900 dark:text-white">${plan.price}</span>
              <span className="text-gray-600 dark:text-gray-300">/{plan.billingCycle}</span>
            </div>
          </div>
          
          {plan.nextBilling && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
              <Calendar className="w-4 h-4 mr-2" />
              Next billing: {new Date(plan.nextBilling).toLocaleDateString()}
            </div>
          )}
          
          {plan.status === 'trial' && plan.trialEndsAt && (
            <div className="flex items-center text-sm text-orange-600 dark:text-orange-400">
              <Calendar className="w-4 h-4 mr-2" />
              Trial ends: {new Date(plan.trialEndsAt).toLocaleDateString()}
            </div>
          )}
        </div>

        {/* Features */}
        <div>
          <h5 className="font-medium text-gray-900 dark:text-white mb-3">Plan Features</h5>
          <div className="space-y-2">
            {plan.features.map((feature, index) => (
              <div key={index} className="flex items-center">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1" 
              onClick={handleManagePlan}
              disabled={isCreatingPortal}
            >
              <Settings className="w-4 h-4 mr-2" />
              {isCreatingPortal ? 'Loading...' : 'Manage Plan'}
            </Button>
            <Button 
              size="sm" 
              className="flex-1" 
              onClick={handleManagePlan}
              disabled={isCreatingPortal}
            >
              <Crown className="w-4 h-4 mr-2" />
              {plan.status === 'trial' ? 'Subscribe' : 'Upgrade'}
            </Button>
          </div>
        </div>

        {/* Billing Info */}
        {plan.status === 'active' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="flex items-center">
              <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Payment Method</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">Manage in billing portal</p>
              </div>
              <Button variant="ghost" size="sm" className="text-blue-600 dark:text-blue-400" onClick={handleManagePlan}>
                Update
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
} 